import { readonly, shallowRef } from 'vue';
import type { TranslateResult } from 'vue-i18n';

type BreadcrumbLabel = string | number | TranslateResult | (() => string | number | TranslateResult | null | undefined);
type BreadcrumbAttrs = Record<string, unknown> | null | undefined | (() => Record<string, unknown> | null | undefined);

export interface Breadcrumb {
    key?: string | number;
    label?: BreadcrumbLabel;
    component?: unknown;
    attrs?: BreadcrumbAttrs;

    [key: string]: unknown;
}

export interface PageBreadcrumb extends Breadcrumb {
    key: string;
}

const breadcrumbs = shallowRef<PageBreadcrumb[]>([]);

function isVisibleBreadcrumb(breadcrumb: Breadcrumb | null | undefined): breadcrumb is Breadcrumb {
    return !!breadcrumb && (!!breadcrumb.label || !!breadcrumb.component);
}

function normalizeBreadcrumbs(value: Breadcrumb | Breadcrumb[] | null | undefined) {
    if (!value) return [];

    return (Array.isArray(value) ? value : [value]).filter(isVisibleBreadcrumb);
}

function getBreadcrumbKey(breadcrumb: Breadcrumb, index: number) {
    return String(breadcrumb.key ?? index);
}

function updateBreadcrumb(target: PageBreadcrumb, source: Breadcrumb) {
    const component = target.component;

    Object.keys(target).forEach((key) => {
        if (key === 'key' || key in source) return;

        delete target[key];
    });

    Object.assign(target, source);

    if (component && source.component) {
        target.component = component;
    }
}

function setBreadcrumbs(value: Breadcrumb | Breadcrumb[] | null | undefined) {
    const previous = new Map(breadcrumbs.value.map(breadcrumb => [breadcrumb.key, breadcrumb]));

    breadcrumbs.value = normalizeBreadcrumbs(value).map((breadcrumb, index) => {
        const key = getBreadcrumbKey(breadcrumb, index);
        const previousBreadcrumb = previous.get(key);

        if (previousBreadcrumb) {
            updateBreadcrumb(previousBreadcrumb, breadcrumb);

            return previousBreadcrumb;
        }

        return {
            ...breadcrumb,
            key,
        };
    });
}

function clearBreadcrumbs() {
    breadcrumbs.value = [];
}

export function getBreadcrumbLabel(breadcrumb: Breadcrumb) {
    return typeof breadcrumb.label === 'function' ? breadcrumb.label() : breadcrumb.label;
}

export function getBreadcrumbAttrs(breadcrumb: Breadcrumb) {
    return typeof breadcrumb.attrs === 'function' ? breadcrumb.attrs() : breadcrumb.attrs;
}

export default function usePageBreadcrumbs() {
    return {
        breadcrumbs: readonly(breadcrumbs),
        setBreadcrumbs,
        clearBreadcrumbs,
    };
}
